import { z } from 'zod';

const customFieldPrefix = 'customFields';
const presetFieldPrefix = '_preset';

export const jsonSchemaFieldSchema = z.object({
    enum: z.array(z.string()).optional(),
    format: z.enum(['date', 'email', 'uri']).optional(),
    type: z.enum(['boolean', 'integer', 'number', 'string']).optional(),
});

export const jsonSchemaObjectSchema = z.object({
    additionalProperties: z.boolean().optional(),
    properties: z.record(z.string(), jsonSchemaFieldSchema),
    type: z.enum(['object']).optional(),
});

export const crmCustomFieldsConfigSchema = z.object({
    address: jsonSchemaObjectSchema.optional(),
    contact: jsonSchemaObjectSchema.optional(),
    customer: jsonSchemaObjectSchema.optional(),
});

export const crmDisplayFieldFormatSchema = z.enum([
    'birthday',
    'currency',
    'date',
    'email',
    'multiline',
    'number',
    'percent',
    'uri',
    'yes-no',
]);

export const crmDisplayConfigField = z.object({
    /**
     * Renders the field at half width on a two-column surface, by default.
     * compact: true would render the field at half-width explicitly. False would render it full-width.
     */
    compact: z.boolean().optional(),
    decimals: z.number().int().optional(),
    format: crmDisplayFieldFormatSchema.optional(),
    label: z.string().optional(),
    options: z.array(z.string()).optional(),
    path: z.string(),
    placeholder: z.string().optional(),
    writable: z.boolean().optional(),
});

export const crmDisplayConfigTab = z.object({
    label: z.string().optional(),
    labelKey: z.string().optional(),
    fields: z.array(crmDisplayConfigField),
});

export const crmDisplayConfigAddressSchema = z.object({
    editFields: z.array(crmDisplayConfigField).optional(),
    fields: z.array(crmDisplayConfigField).optional(),
    listFields: z.array(crmDisplayConfigField).optional(),
});

export const crmDisplayConfigContactSchema = z.object({
    editFields: z.array(crmDisplayConfigField).optional(),
    fields: z.array(crmDisplayConfigField).optional(),
    listFields: z.array(crmDisplayConfigField).optional(),
});

export const crmDisplayConfigCustomerSchema = z.object({
    tabs: z.array(crmDisplayConfigTab),
});

export const crmDisplayConfigSchema = z.object({
    address: crmDisplayConfigAddressSchema.optional(),
    contact: crmDisplayConfigContactSchema.optional(),
    customer: crmDisplayConfigCustomerSchema.optional(),
});

export type CrmCustomFieldsConfig = z.infer<typeof crmCustomFieldsConfigSchema>;
export type CrmDisplayConfig = z.infer<typeof crmDisplayConfigSchema>;
export type CrmDisplayConfigAddress = z.infer<typeof crmDisplayConfigAddressSchema>;
export type CrmDisplayConfigContact = z.infer<typeof crmDisplayConfigContactSchema>;

export type CrmDisplayConfigField = z.infer<typeof crmDisplayConfigField>;

export type CrmDisplayConfigTab = z.infer<typeof crmDisplayConfigTab>;

export type JsonSchemaField = z.infer<typeof jsonSchemaFieldSchema>;
export type JsonSchemaObject = z.infer<typeof jsonSchemaObjectSchema>;

export type CrmEntityType = keyof CrmDisplayConfig;

export const presetFields = {
    address: {
        formatted: '/_preset/address/formatted',
    },
    customer: {
        address: `/_preset/customer/address`,
        creditLimit: '/_preset/customer/creditLimit',
        internalSalesReps: '/_preset/customer/internalSalesReps',
        externalSalesReps: '/_preset/customer/externalSalesReps',
        gridCreditLimit: '/_preset/customer/gridCreditLimit',
    },
} satisfies Partial<Record<CrmEntityType, Record<string, `/_preset/${CrmEntityType}/${string}`>>>;

export const platoNativeFieldSchemas = {
    address: {
        canReceiveGoodsAtNight: { type: 'boolean' },
        canReceiveParcels: { type: 'boolean' },
        comment: { type: 'string' },
        isPreferredShippingAddress: { type: 'boolean' },
    },
    contact: {
        birthday: { type: 'string' },
        comment: { type: 'string' },
        department: { type: 'string' },
        role: { type: 'string' },
    },
    customer: {
        annualSalesPotential: { type: 'number' },
        associationMemberships: { type: 'string' },
        branch: { type: 'string' },
        creditLimitAllowed: { type: 'number' },
        creditLimitUsed: { type: 'number' },
        customerType: { type: 'string' },
        dunningLevel: { type: 'string' },
        emailAddress: { type: 'string', format: 'email' },
        employeeCount: { type: 'integer' },
        facebookUrl: { type: 'string', format: 'uri' },
        faxNumber: { type: 'string' },
        linkedInUrl: { type: 'string', format: 'uri' },
        orderBlock: { type: 'string' },
        ownExhibition: { type: 'string' },
        paymentTerms: { type: 'string' },
        phoneNumber: { type: 'string' },
        priceGroup: { type: 'string' },
        region: { type: 'string' },
        taxNumber: { type: 'string' },
        websiteUrl: { type: 'string', format: 'uri' },
    },
} satisfies Record<CrmEntityType, Record<string, JsonSchemaField>>;

const platoNativeFieldDisplay: Record<CrmEntityType, Record<string, Partial<CrmDisplayConfigField>>> = {
    address: {},
    contact: {
        birthday: { format: 'birthday' },
    },
    customer: {
        annualSalesPotential: { format: 'currency' },
        creditLimitAllowed: { format: 'currency' },
        creditLimitUsed: { format: 'currency' },
        potentialReachedTtm: { format: 'percent' },
        potentialReachedYtd: { format: 'percent' },
        quoteWinRateTtm: { format: 'percent' },
        revenueTtm: { format: 'currency' },
        revenueYtd: { format: 'currency' },
        unbilledLineItemTotal: { format: 'currency' },
        unbilledLineItemTotalDiscounted: { format: 'currency' },
    },
};

export const defaultDisplayConfigs = {
    address: {
        fields: [{ path: '/_preset/address/formatted' }],
    },
    contact: {
        fields: [
            { path: '/salutation', compact: true },
            { path: '/name' },
            { path: '/email', compact: true },
            { path: '/phone', compact: true },
            { path: '/mobile', compact: true },
            { path: '/fax', compact: true },
        ],
    },
    customer: {
        tabs: [
            {
                fields: [
                    { path: '/_preset/customer/address' },
                    { path: '/industrySector' },
                    { path: '/_preset/customer/externalSalesReps' },
                    { path: '/_preset/customer/internalSalesReps' },
                    { path: '/_preset/customer/creditLimit' },
                    { path: '/lastOrderDate' },
                    { path: '/firstOrderDate' },
                ],
                labelKey: 'general',
            },
            {
                fields: [
                    { path: '/revenueTtm' },
                    { path: '/revenueYtd' },
                    { path: '/openQuoteCount' },
                    { path: '/openOrderCount' },
                    { path: '/quoteWinRateTtm' },
                ],
                labelKey: 'sales',
            },
            {
                fields: [
                    { path: '/unbilledLineItemTotal' },
                    { path: '/dunningLevel', label: 'Dunning level' },
                ],
                labelKey: 'accounting',
            },
        ],
    },
} satisfies Partial<CrmDisplayConfig>;

/**
 * Get the validation schema for the given path.
 */
export function lookupCrmFieldSchema(
    entity: CrmEntityType,
    path: string,
    customFields: CrmCustomFieldsConfig | undefined,
): JsonSchemaField | undefined {
    if (!path.startsWith('/')) {
        return;
    }
    const parts = path.slice(1).split('/');
    if (parts.length === 1) {
        const schemas = platoNativeFieldSchemas[entity] as Record<string, JsonSchemaField> | undefined;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- already checked bounds
        return schemas?.[parts[0]!];
    } else if (parts.length === 2 && parts[0] === customFieldPrefix) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return customFields?.[entity]?.properties[parts[1]!];
    }
}

/**
 * Apply defaults from built-in values and validation schema.
 */
export function lookupCrmFieldDisplay(
    entity: CrmEntityType,
    mergeConfig: CrmDisplayConfigField,
    customFields: CrmCustomFieldsConfig | undefined,
): CrmDisplayConfigField {
    if (!mergeConfig.path.startsWith('/')) {
        return mergeConfig;
    }

    const result: Partial<CrmDisplayConfigField> = {};
    const schema = lookupCrmFieldSchema(entity, mergeConfig.path, customFields);

    if (schema?.type === 'number') {
        result.format = 'number';
    } else if (schema?.type === 'boolean') {
        result.format = 'yes-no';
    }
    if (schema?.format) {
        result.format = schema.format;
    }
    if (schema?.enum) {
        result.options = schema.enum;
    }

    const parts = mergeConfig.path.slice(1).split('/');
    if (parts.length === 1) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        Object.assign(result, platoNativeFieldDisplay[entity][parts[0]!]);
    }

    // field can't be writable if no schema exists
    return Object.assign(result, mergeConfig, { writable: schema !== undefined && mergeConfig.writable });
}

const numberFormatStyles: Intl.NumberFormatOptionsStyle[] = ['currency', 'decimal', 'percent', 'unit'];

export function isNumberFormatStyle(format: string): format is Intl.NumberFormatOptionsStyle {
    return numberFormatStyles.includes(format as Intl.NumberFormatOptionsStyle);
}

export function getCrmFieldNumberFormat(
    config: CrmDisplayConfigField,
    defaults?: Intl.NumberFormatOptions,
): Intl.NumberFormatOptions {
    return {
        ...defaults,
        style: numberFormatStyles.find((x) => x === config.format),
        minimumFractionDigits: config.decimals ?? defaults?.minimumFractionDigits ?? 0,
        maximumFractionDigits: config.decimals ?? defaults?.maximumFractionDigits ?? 0,
    };
}

export function isPresetFieldPath(path: string, entity?: CrmEntityType): boolean {
    if (entity) {
        return path.startsWith(`/${presetFieldPrefix}/${entity}/`);
    }
    return path.startsWith(`/${presetFieldPrefix}/`);
}
